import { useEffect, useMemo, useRef, useState } from 'react';

import { formatHours, type UserDto, type UserHistoryEntry } from '@time-tracking/shared';

import { useUserHistory } from '../../api/approvals.js';
import { useSearchUsers } from '../../api/users.js';
import './user-history.css';

// ---- Types ------------------------------------------------------------------

type StatusFilter = 'all' | 'approved' | 'submitted' | 'returned' | 'draft';
type DateRange = 'all' | '8' | '12';

// ---- Helpers ----------------------------------------------------------------

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtWeekRange(weekStartDate: string, weekEndDate: string): string {
  const parse = (s: string): Date => {
    const parts = s.split('-').map(Number);
    // Use UTC noon to avoid DST artifacts
    return new Date(Date.UTC(parts[0] ?? 0, (parts[1] ?? 1) - 1, parts[2] ?? 1, 12));
  };
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const fmtYear = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `${fmt.format(parse(weekStartDate))} – ${fmtYear.format(parse(weekEndDate))}`;
}

function isoToDate(iso: string): Date {
  return new Date(iso + 'T12:00:00Z');
}

// ---- Sub-components ---------------------------------------------------------

function HistPill({ status }: { status: UserHistoryEntry['status'] }) {
  const labels: Record<UserHistoryEntry['status'], string> = {
    approved: 'Approved',
    submitted: 'Submitted',
    returned: 'Returned',
    draft: 'Draft',
  };
  return (
    <span className={`hist-pill hist-pill--${status}`} aria-label={`Status: ${labels[status]}`}>
      <span className="hist-pill__dot" aria-hidden="true" />
      {labels[status]}
    </span>
  );
}

function Avatar({ initials, size = 36 }: { initials: string; size?: number }) {
  return (
    <span
      className="uhv-avatar"
      style={{ width: size, height: size, fontSize: size <= 36 ? undefined : 'var(--fs-14)' }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

// ---- HistoryTable -----------------------------------------------------------

function HistoryTable({ rows }: { rows: UserHistoryEntry[] }) {
  return (
    <div className="uhv-table" role="table" aria-label="Timesheet history">
      <div className="uhv-tr uhv-tr--head" role="row">
        <div className="uhv-td" role="columnheader">
          Week
        </div>
        <div className="uhv-td" role="columnheader">
          Status
        </div>
        <div className="uhv-td uhv-td--right" role="columnheader">
          Hours
        </div>
        <div className="uhv-td" role="columnheader">
          Submitted
        </div>
        <div className="uhv-td" role="columnheader">
          Approved
        </div>
      </div>
      {rows.map((e) => {
        const isUnder = e.totalMinutes < e.weeklyMinuteTarget;
        return (
          <div className="uhv-tr" role="row" key={e.timesheetId}>
            <div className="uhv-td uhv-td--wk" role="cell">
              {fmtWeekRange(e.weekStartDate, e.weekEndDate)}
            </div>
            <div className="uhv-td" role="cell">
              <HistPill status={e.status} />
            </div>
            <div className="uhv-td uhv-td--right" role="cell">
              <span className={`uhv-hrs${isUnder ? ' uhv-hrs--under' : ''}`}>
                {formatHours(e.totalMinutes)}h
              </span>
              <span className="uhv-target"> / {formatHours(e.weeklyMinuteTarget)}h</span>
            </div>
            <div className="uhv-td uhv-td--muted" role="cell">
              {fmtDate(e.submittedAt)}
            </div>
            <div className="uhv-td uhv-td--muted" role="cell">
              {fmtDate(e.approvedAt)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Main component ---------------------------------------------------------

/** A person to open the view already scoped to, e.g. handed over from the drawer. */
export interface HistoryInitialUser {
  userId: string;
  displayName: string;
  /** Server-computed avatar initials, carried from whatever DTO handed this person over. */
  initials: string;
  utEid: string | null;
}

interface UserHistoryViewProps {
  isAdmin: boolean;
  /**
   * Preselects this person on mount and whenever the identity changes, skipping
   * the search step. The view still owns its own selection afterwards — picking
   * someone else from search works exactly as before.
   */
  initialUser?: HistoryInitialUser | undefined;
}

export function UserHistoryView({ isAdmin, initialUser }: UserHistoryViewProps) {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(initialUser?.userId ?? null);
  const [selectedUserName, setSelectedUserName] = useState<string>(initialUser?.displayName ?? '');
  // Seeded from the DTO that named this person (inbox entry or search result) so the
  // header avatar has letters before the history request lands.
  const [selectedUserInitials, setSelectedUserInitials] = useState<string>(
    initialUser?.initials ?? '',
  );
  const [selectedUserEid, setSelectedUserEid] = useState<string | null>(initialUser?.utEid ?? null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [range, setRange] = useState<DateRange>('all');
  const searchRef = useRef<HTMLInputElement>(null);

  // Re-scope when the caller hands over a different person (e.g. the approver used
  // "View full history" on a second drawer without leaving the History view).
  // Keyed on the id alone — depending on the whole object would re-run on every
  // parent render and fight the user's own selection.
  const initialUserId = initialUser?.userId ?? null;
  const initialUserName = initialUser?.displayName ?? '';
  const initialUserInitials = initialUser?.initials ?? '';
  const initialUserEid = initialUser?.utEid ?? null;
  useEffect(() => {
    if (initialUserId === null) return;
    setSelectedUserId(initialUserId);
    setSelectedUserName(initialUserName);
    setSelectedUserInitials(initialUserInitials);
    setSelectedUserEid(initialUserEid);
    setQuery('');
    setStatusFilter('all');
    setRange('all');
  }, [initialUserId, initialUserName, initialUserInitials, initialUserEid]);

  const searchQuery = useSearchUsers(query, { allowEmpty: true });
  const historyQuery = useUserHistory(selectedUserId);

  const entries = historyQuery.data?.entries ?? [];

  const filtered = useMemo(() => {
    let h = entries;
    if (statusFilter !== 'all') {
      h = h.filter((e) => e.status === statusFilter);
    }
    if (range !== 'all') {
      const n = parseInt(range, 10);
      // Compute cutoff: n weeks back from the most-recent Monday
      const today = new Date();
      const day = today.getUTCDay();
      const diff = day === 0 ? -6 : 1 - day;
      const thisMonday = new Date(today);
      thisMonday.setUTCDate(today.getUTCDate() + diff);
      const cutoffMs = thisMonday.getTime() - n * 7 * 24 * 60 * 60 * 1000;
      h = h.filter((e) => isoToDate(e.weekStartDate).getTime() >= cutoffMs);
    }
    return h;
  }, [entries, statusFilter, range]);

  const summary = useMemo(() => {
    if (!entries.length) return null;
    const approved = entries.filter((e) => e.status === 'approved').length;
    const returned = entries.filter((e) => e.status === 'returned').length;
    const submitted = entries.filter((e) => e.status !== 'draft');
    const avgMinutes =
      submitted.length > 0
        ? submitted.reduce((s, e) => s + e.totalMinutes, 0) / submitted.length
        : 0;
    return { total: entries.length, approved, returned, avgMinutes };
  }, [entries]);

  const handleSelectUser = (user: UserDto) => {
    setSelectedUserId(user.userId);
    setSelectedUserName(user.displayName);
    setSelectedUserInitials(user.initials);
    setSelectedUserEid(user.utEid);
    setQuery('');
    setStatusFilter('all');
    setRange('all');
  };

  const handleChangePerson = () => {
    setSelectedUserId(null);
    setSelectedUserName('');
    setSelectedUserInitials('');
    setSelectedUserEid(null);
    setStatusFilter('all');
    setRange('all');
    // Focus the search input after state update
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  // ---- Empty state: user picker ----
  if (!selectedUserId) {
    // Show all accessible users immediately; search filters the list.
    const allUsers = searchQuery.data ?? [];
    return (
      <div className="uhv">
        <div className="uhv-picker-card">
          <div className="uhv-picker-head">
            <h2>Submission history</h2>
            <p>Select a person to review their weekly timesheets. Read-only.</p>
          </div>
          <div className="uhv-search">
            <span className="uhv-search__icon" aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <label htmlFor="uhv-search-input" className="sr-only">
              Search for a user
            </label>
            <input
              id="uhv-search-input"
              ref={searchRef}
              type="text"
              autoFocus
              placeholder={
                isAdmin ? 'Search all users by name or EID…' : 'Search your team by name or EID…'
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search for a user"
              aria-controls="uhv-results-list"
              aria-autocomplete="list"
            />
          </div>
          <ul id="uhv-results-list" className="uhv-results" role="listbox" aria-label="User list">
            {searchQuery.isLoading && <li className="uhv-results__loading">Loading…</li>}
            {allUsers.map((user) => (
              <li
                key={user.userId}
                role="option"
                aria-selected={false}
                tabIndex={0}
                className="uhv-result"
                onClick={() => handleSelectUser(user)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectUser(user);
                  }
                }}
              >
                <Avatar initials={user.initials} />
                <div className="who">
                  <div className="nm">{user.displayName}</div>
                  <div className="meta">{user.utEid ?? user.email}</div>
                </div>
                <svg
                  className="chev"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </li>
            ))}
            {query.trim().length >= 1 && allUsers.length === 0 && (
              <li className="uhv-empty" role="option" aria-selected={false}>
                No users match &ldquo;{query}&rdquo;.
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  }

  // ---- Selected state: header + filters + table ----
  // Once the history loads, its own initials win; until then, the seeded value.
  const initials =
    historyQuery.data?.userId === selectedUserId
      ? historyQuery.data.initials
      : selectedUserInitials;

  const statusFilters: { id: StatusFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'approved', label: 'Approved' },
    { id: 'submitted', label: 'Submitted' },
    { id: 'returned', label: 'Returned' },
    { id: 'draft', label: 'Draft' },
  ];

  return (
    <div className="uhv">
      {/* Identity header */}
      <div className="uhv-selected-head">
        <Avatar initials={initials} size={40} />
        <div className="who">
          <div className="nm">{selectedUserName}</div>
          <div className="meta">{selectedUserEid ?? ''}</div>
        </div>
        <button type="button" className="btn btn--secondary" onClick={handleChangePerson}>
          Change person
        </button>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="uhv-summary" aria-label="Summary statistics">
          <div className="stat">
            <div className="n">{summary.total}</div>
            <div className="l">weeks on file</div>
          </div>
          <div className="stat">
            <div className="n">{summary.approved}</div>
            <div className="l">approved</div>
          </div>
          <div className="stat">
            <div className="n">{summary.returned}</div>
            <div className="l">returned</div>
          </div>
          <div className="stat">
            <div className="n">{formatHours(summary.avgMinutes)}h</div>
            <div className="l">avg / week</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="uhv-filters">
        <div className="uhv-status-chips" role="tablist" aria-label="Filter by status">
          {statusFilters.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={statusFilter === f.id}
              onClick={() => setStatusFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <label className="uhv-range">
          <span>Range</span>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as DateRange)}
            aria-label="Date range"
          >
            <option value="all">All time</option>
            <option value="8">Last 8 weeks</option>
            <option value="12">Last 12 weeks</option>
          </select>
        </label>
      </div>

      {/* Loading state */}
      {historyQuery.isLoading && (
        <div
          className="uhv-loading"
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading timesheet history"
        >
          <div className="skeleton skeleton--line" aria-hidden="true" />
          <div className="skeleton skeleton--line" aria-hidden="true" />
          <div className="skeleton skeleton--line" aria-hidden="true" />
        </div>
      )}

      {/* Error state */}
      {historyQuery.isError && (
        <div className="uhv-noresults" role="alert">
          Failed to load history. Please try again.
        </div>
      )}

      {/* Results */}
      {!historyQuery.isLoading && !historyQuery.isError && (
        <>
          {filtered.length === 0 ? (
            <div className="uhv-noresults" role="status">
              {entries.length === 0
                ? 'No timesheets found for this user.'
                : 'No timesheets match these filters.'}
            </div>
          ) : (
            <HistoryTable rows={filtered} />
          )}
        </>
      )}
    </div>
  );
}
